import { ApplicationGroup, MigrationWave } from '@amzn/awscat-portfolio-assessment-types';
import { Alert, Box, Button, Modal, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useState } from 'react';

import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { format } from '../../../common/util';
import ConfirmationModal from '../../common/ConfirmationModal';
import { groupTabColumnDefinitions } from './constants';

interface Props {
    groups: ApplicationGroup[];
    waves: MigrationWave[];
    addToWave: (groupIds: string[], waveId: string) => Promise<void>;
    removeFromWave: (groupIds: string[]) => Promise<void>;
}

function GroupsTab({groups, waves, addToWave, removeFromWave}: Props) {
    const [ selectedGroups, setSelectedGroups ] = useState<ApplicationGroup[]>([]);
    const [ addToWaveVisible, setAddToWaveVisible ] = useState(false);
    const [ removeFromWaveVisible, setRemoveFromWaveVisible ] = useState(false);
    const [ loading, setLoading ] = useState(false);
   
    async function onAddToWave(waveId: string) {
        if (!selectedGroups.length) {
            return;
        }
        setLoading(true);
        await addToWave(selectedGroups.map(group => group.applicationGroupId), waveId);
        setLoading(false);
        setAddToWaveVisible(false);
    }
    async function onRemoveFromWave() {
        if (!selectedGroups.length) {
            return;
        }
        setLoading(true);
        await removeFromWave(selectedGroups.map(group => group.applicationGroupId));
        setLoading(false);
        setRemoveFromWaveVisible(false);
    }
    const wavesList = waves.map(wave => {
        const label = `${wave.name} - ${wave.totalApplications} ${constants.APPLICATIONS}`
            + ` - ${wave.totalServers} ${constants.SERVERS} - ${wave.totalDatabases} ${constants.DATABASES}`;
        return <Button key={wave.migrationWaveId} variant='link' onClick={() => onAddToWave(wave.migrationWaveId)}>{label}</Button>;
    });

    return (
        <Box>
            <AWSCATTable
                headerActions={
                    <SpaceBetween direction='horizontal' size='m'>
                        <Button onClick={() => setAddToWaveVisible(true)} disabled={!selectedGroups.length || !waves.length}>{constants.ADD_TO_WAVE}</Button>
                        <Button onClick={() => setRemoveFromWaveVisible(true)} disabled={!selectedGroups.length}>{constants.REMOVE_FROM_WAVE}</Button>
                    </SpaceBetween>
                }
                columnDefinitions={groupTabColumnDefinitions}
                items={groups}
                resourceName={constants.GROUPS}
                selectionType='multi'
                onSelectionChange={setSelectedGroups as () => void}
                autoGenerateVisibleContent
                useDefaultDownload
            />
            <Modal header={constants.ADD_TO_WAVE} visible={addToWaveVisible} onDismiss={() => setAddToWaveVisible(false)}>
                {loading ? <Spinner size='big' />: wavesList}
            </Modal>
            <ConfirmationModal
                header={constants.REMOVE_GROUP_FROM_WAVE}
                visible={removeFromWaveVisible}
                onDismiss={() => setRemoveFromWaveVisible(false)}
                onConfirm={() => onRemoveFromWave()}
                confirmButton={constants.REMOVE_BUTTON_TEXT}
                confirmLoading={loading}>
                <Alert header={constants.THIS_ACTION_CANNOT_BE_UNDONE}>
                    {format(constants.YOU_ARE_ABOUT_TO_REMOVE_GROUPS_FROM_THEIR_WAVE, [`${selectedGroups.length}`])}
                        . {constants.FORM_DELETE_ERROR_PROCEED}
                </Alert>
            </ConfirmationModal>
        </Box>
    );
}

export default GroupsTab;
